import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, MapPin, X } from 'lucide-react';
const firstNames = ['María', 'Lucía', 'Sofía', 'Camila', 'Valentina', 'Martina', 'Florencia', 'Agustina', 'Carolina', 'Julieta', 'Mariana', 'Daniela', 'Juan', 'Martín', 'Federico', 'Mateo', 'Lucas', 'Tomás', 'Nicolás', 'Sebastián', 'Diego', 'Gonzalo', 'Matías', 'Andrés', 'Rodrigo', 'Pablo', 'Leandro', 'Joaquín'];
const lastNames = ['G.', 'M.', 'R.', 'S.', 'L.', 'P.', 'F.', 'C.', 'D.', 'A.', 'B.', 'H.', 'V.', 'N.'];
const cities = ['Buenos Aires, CABA', 'Córdoba Capital', 'Rosario, Santa Fe', 'Mendoza Capital', 'La Plata, Buenos Aires', 'Mar del Plata, Buenos Aires', 'San Miguel de Tucumán', 'Salta Capital', 'Neuquén Capital', 'Bahía Blanca, Buenos Aires', 'San Juan Capital', 'Resistencia, Chaco', 'Posadas, Misiones', 'Santiago del Estero', 'Corrientes Capital', 'Paraná, Entre Ríos', 'San Fernando del Valle de Catamarca', 'Río Cuarto, Córdoba', 'San Luis Capital', 'Comodoro Rivadavia, Chubut', 'Bariloche, Río Negro', 'Ushuaia, Tierra del Fuego', 'Quilmes, Buenos Aires', 'Tigre, Buenos Aires', 'Morón, Buenos Aires', 'Avellaneda, Buenos Aires', 'San Isidro, Buenos Aires', 'Tandil, Buenos Aires'];
const products = [{
  name: 'Más Allá de la Voluntad',
  label: 'Sistema completo'
}, {
  name: 'Pack Premium',
  label: 'Sistema + Volúmenes 7 y 8'
}];
const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const buildNotification = () => ({
  id: Math.random().toString(36).slice(2),
  name: `${pickRandom(firstNames)} ${pickRandom(lastNames)}`,
  city: pickRandom(cities),
  product: pickRandom(products),
  minutesAgo: Math.floor(Math.random() * 8) + 1 // 1-8 minutes
});
type Notification = ReturnType<typeof buildNotification>;
export function PurchaseNotification() {
  const [current, setCurrent] = useState<Notification | null>(null);
  useEffect(() => {
    let visibleTimer: ReturnType<typeof setTimeout> | undefined;
    let nextTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const showNext = () => {
      if (cancelled) return;
      setCurrent(buildNotification());
      // Visible for ~5 seconds, then hide
      visibleTimer = setTimeout(() => {
        if (cancelled) return;
        setCurrent(null);
        // Wait random 40-60s before the next one
        const delay = (40 + Math.random() * 20) * 1000;
        nextTimer = setTimeout(showNext, delay);
      }, 5000);
    };
    // First notification appears 8s after mount so it doesn't feel staged
    nextTimer = setTimeout(showNext, 8000);
    return () => {
      cancelled = true;
      if (visibleTimer) clearTimeout(visibleTimer);
      if (nextTimer) clearTimeout(nextTimer);
    };
  }, []);
  return <div aria-live="polite" className="fixed bottom-3 left-3 md:bottom-5 md:left-5 z-[60] pointer-events-none max-w-[calc(100vw-1.5rem)] sm:max-w-xs" data-id="element-299">
      <AnimatePresence data-id="element-300">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-gold/30 overflow-hidden" data-id="element-301">
            <div className="flex items-start gap-2.5 p-3 md:p-3.5" data-id="element-302">
              <div className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-303">
                <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-green-600" strokeWidth={2.5} data-id="element-304" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-305">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-306">
                  <span className="text-[9px] md:text-[10px] font-bold text-green-700 uppercase tracking-wide leading-none" data-id="element-307">
                    Compra verificada
                  </span>
                </div>
                <p className="text-xs md:text-sm text-navy-dark leading-snug" data-id="element-308">
                  <span className="font-bold" data-id="element-309">{current.name}</span>{' '}
                  <span className="text-gray-700" data-id="element-310">compró</span>{' '}
                  <span className="font-semibold text-navy-dark" data-id="element-311">
                    {current.product.name}
                  </span>
                </p>
                <div className="flex items-center gap-1 mt-1 text-[10px] md:text-xs text-gray-500" data-id="element-312">
                  <MapPin className="w-3 h-3 flex-shrink-0" strokeWidth={2} data-id="element-313" />
                  <span className="truncate" data-id="element-314">{current.city}</span>
                  <span className="mx-1" data-id="element-315">•</span>
                  <span className="whitespace-nowrap" data-id="element-316">
                    Hace {current.minutesAgo} min
                  </span>
                </div>
              </div>

              <button onClick={() => setCurrent(null)} aria-label="Cerrar notificación" className="flex-shrink-0 -mr-1 -mt-1 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-317">
                <X className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-318" />
              </button>
            </div>

            <div className="h-1 bg-gold/10" data-id="element-319">
              <motion.div key={current.id + '-bar'} initial={{
            width: '100%'
          }} animate={{
            width: '0%'
          }} transition={{
            duration: 5,
            ease: 'linear'
          }} className="h-full bg-gold" data-id="element-320" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}